import {AxiosError} from "axios";
import {useState} from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {PGPApi, PGPKeyGenerationType} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

const pgpApi = new PGPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PGPCreateNewKey {
    onClose : OnClose
}

export const PGPCreateNewKeyDialog = ({onClose} : PGPCreateNewKey) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * Email address for the new key
     */
    const [email, setEmail] = useState<string>();
    /**
     * Name for the new key
     */
    const [name, setName] = useState<string>();
    /**
     * Key type for the new key
     */
    const [keyType, setKeyType] = useState<PGPKeyGenerationType>(PGPKeyGenerationType.Rsa3072);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while creating a key to show a spinner on the import button
     */
    const [creating, setCreating] = useState<boolean>(false);

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const createKey = () =>
    {
        if (email === undefined || email.length === 0) {
            throw new Error("Email is not set")
        }

        setCreating(true);

        pgpApi.pGPControllerGenerateSecretKeyRing(email, name??"", keyType)
            .then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error creating PGP key", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setCreating(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("pgp.create-new-pgp-key")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Control size="sm"
                                      aria-label={t("pgp.email-address")}
                                      type="text"
                                      placeholder={t("pgp.email-address")}
                                      value={email}
                                      onChange={e => setEmail(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Select size="sm"
                                     aria-label={t("pgp.key-type")}
                                     value={keyType}
                                     onChange={(e) => setKeyType(Utils.stringToEnum(PGPKeyGenerationType, e.target.value as PGPKeyGenerationType))}>
                            <option value="RSA_1024">RSA 1024</option>
                            <option value="RSA_2048">RSA 2048</option>
                            <option value="RSA_3072">RSA 3072</option>
                            <option value="RSA_4096">RSA 4096</option>
                            <option value="ECC_NIST_P_256">ECC NIST P256</option>
                            <option value="ECC_NIST_P_384">ECC NIST P384</option>
                            <option value="ECC_NIST_P_521">ECC NIST P521</option>
                            <option value="ECC_BRAINPOOL_P_256">ECC Brainpool P256</option>
                            <option value="ECC_BRAINPOOL_P_384">ECC Brainpool P384</option>
                            <option value="ECC_BRAINPOOL_P_512">ECC Brainpool P5124</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Control size="sm"
                                      aria-label={t("pgp.name")}
                                      type="text"
                                      placeholder={t("pgp.name")}
                                      value={name}
                                      onChange={e => setName(e.target.value)}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={createKey}
                        disabled={email === undefined || email.length === 0}>
                    {creating && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {creating ? t("pgp.creating-key") : t("pgp.create-key")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
